import React, { useRef, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  FormGroup
} from '@mui/material';
import api from '../../utils/api';
import {
  MAILCHIMP_MARKETING_CONSENT_FIELD,
  DEFAULT_MAILCHIMP_CONSENT_MESSAGE
} from '../../constants/mailchimpFormConsent';

function PublicLeadForm({ formData, _businessId }) {
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const honeypotRef = useRef(null);

  if (!formData || !formData.fields || formData.fields.length === 0) {
    return null;
  }

  // Sort fields by order
  const sortedFields = [...formData.fields].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleChange = (fieldName, fieldType = 'text') => (e) => {
    let value = e.target.value;
    if (fieldType === 'checkbox') {
      value = Boolean(e.target.checked);
    }
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleCheckboxOptionToggle = (fieldName, optionValue) => (e) => {
    const checked = Boolean(e.target.checked);
    setFormValues((prev) => {
      const current = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];
      const next = checked
        ? [...new Set([...current, optionValue])]
        : current.filter((value) => value !== optionValue);
      return {
        ...prev,
        [fieldName]: next
      };
    });
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    sortedFields.forEach((field) => {
      const value = formValues[field.name];
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      const isUnchecked = field.type === 'checkbox' && !Array.isArray(value) && value !== true;
      if (field.required && (value == null || value === '' || isEmptyArray || isUnchecked)) {
        newErrors[field.name] = `${field.label} is required`;
      }
      
      // Email validation
      if (field.type === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          newErrors[field.name] = 'Please enter a valid email address';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    try {
      const response = await api.post(`/api/public/leads/${formData._id}`, {
        ...formValues,
        idotoo_hp: honeypotRef.current?.value || ''
      });
      
      if (response.data.success) {
        setSubmitSuccess(true);
        setFormValues({});
        
        // If redirect URL is provided, redirect after a delay
        if (response.data.redirect) {
          setTimeout(() => {
            window.location.href = response.data.redirect;
          }, 2000);
        }
      } else {
        setSubmitError(response.data.message || 'Failed to submit form. Please try again.');
      }
    } catch (error) {
      setSubmitError(
        error.response?.data?.message || 'Failed to submit form. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const fieldValue = formValues[field.name] ?? field.defaultValue ?? '';
    const isEmpty = Array.isArray(fieldValue)
      ? fieldValue.length === 0
      : typeof fieldValue === 'string'
        ? fieldValue.trim() === ''
        : !fieldValue;
    const showRequiredMessage = field.required && isEmpty && !errors[field.name];
    
    const commonProps = {
      fullWidth: true,
      label: field.label,
      placeholder: field.placeholder,
      value: fieldValue,
      onChange: handleChange(field.name, field.type),
      error: !!errors[field.name],
      helperText: errors[field.name] || (showRequiredMessage ? 'This field is required' : ''),
      required: field.required,
      sx: { mb: 2 },
    };

    switch (field.type) {
      case 'email':
        return <TextField key={field.name} type="email" {...commonProps} />;
      
      case 'phone':
        return <TextField key={field.name} type="tel" {...commonProps} />;
      
      case 'date':
        return (
          <TextField
            key={field.name}
            type="date"
            {...commonProps}
            InputLabelProps={{ shrink: true }}
          />
        );
      
      case 'textarea':
        return (
          <TextField
            key={field.name}
            multiline
            rows={4}
            {...commonProps}
          />
        );
      
      case 'number':
        return <TextField key={field.name} type="number" {...commonProps} />;
      
      case 'select':
        return (
          <TextField
            key={field.name}
            select
            SelectProps={{
              native: true,
            }}
            {...commonProps}
          >
            <option value="">Select {field.label}</option>
            {(field.options || []).map((option, index) => {
              const opt = typeof option === 'string' ? { label: option, value: option } : (option || {});
              const v = opt.value != null ? String(opt.value) : '';
              const lbl = opt.label != null ? String(opt.label) : v;
              return (
                <option key={index} value={v}>
                  {lbl}
                </option>
              );
            })}
          </TextField>
        );

      case 'radio':
        return (
          <FormControl key={field.name} required={field.required} error={!!errors[field.name]} sx={{ mb: 2 }}>
            <FormLabel>{field.label}</FormLabel>
            <RadioGroup value={fieldValue || ''} onChange={handleChange(field.name, 'radio')}>
              {(field.options || []).map((option, index) => {
                const opt = typeof option === 'string' ? { label: option, value: option } : (option || {});
                const v = opt.value != null ? String(opt.value) : '';
                const lbl = opt.label != null ? String(opt.label) : v;
                return <FormControlLabel key={index} value={v} control={<Radio />} label={lbl} />;
              })}
            </RadioGroup>
            <FormHelperText>{errors[field.name] || ''}</FormHelperText>
          </FormControl>
        );

      case 'checkbox': {
        const options = Array.isArray(field.options) ? field.options : [];
        if (options.length > 0) {
          const selectedValues = Array.isArray(formValues[field.name]) ? formValues[field.name] : [];
          return (
            <FormControl key={field.name} required={field.required} error={!!errors[field.name]} sx={{ mb: 2 }}>
              <FormLabel>{field.label}</FormLabel>
              <FormGroup>
                {options.map((option, index) => {
                  const opt = typeof option === 'string' ? { label: option, value: option } : (option || {});
                  const v = opt.value != null ? String(opt.value) : '';
                  const lbl = opt.label != null ? String(opt.label) : v;
                  return (
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          checked={selectedValues.includes(v)}
                          onChange={handleCheckboxOptionToggle(field.name, v)}
                        />
                      }
                      label={lbl}
                    />
                  );
                })}
              </FormGroup>
              <FormHelperText>{errors[field.name] || ''}</FormHelperText>
            </FormControl>
          );
        }
        return (
          <FormControl key={field.name} required={field.required} error={!!errors[field.name]} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formValues[field.name])}
                  onChange={handleChange(field.name, 'checkbox')}
                />
              }
              label={field.label}
            />
            <FormHelperText>{errors[field.name] || ''}</FormHelperText>
          </FormControl>
        );
      }
      
      default:
        return <TextField key={field.name} {...commonProps} />;
    }
  };

  if (submitSuccess) {
    return (
      <Alert severity="success" sx={{ mb: 2 }}>
        {formData.successMessage || 'Thank you for your enquiry. We will be in touch shortly.'}
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <input
        ref={honeypotRef}
        type="text"
        name="idotoo_hp"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        defaultValue=""
        style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}
      />
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      {sortedFields.map((field) => renderField(field))}

      {formData.mailchimp?.enabled ? (
        <FormControl sx={{ mb: 1, mt: 0 }} component="fieldset" variant="standard">
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(formValues[MAILCHIMP_MARKETING_CONSENT_FIELD])}
                onChange={handleChange(MAILCHIMP_MARKETING_CONSENT_FIELD, 'checkbox')}
              />
            }
            label={String(formData.mailchimp?.consentMessage || '').trim() || DEFAULT_MAILCHIMP_CONSENT_MESSAGE}
          />
        </FormControl>
      ) : null}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={submitting}
        sx={{ mt: 2 }}
      >
        {submitting ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Submitting...
          </>
        ) : (
          formData.styling?.buttonText || 'Submit'
        )}
      </Button>
    </Box>
  );
}

export default PublicLeadForm;

